// src/Dashboard/pages/user/Inscription/MemberCardPage.jsx

import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBirthdayCake,
  FaCheck,
  FaDownload,
  FaIdCard,
  FaPrint,
  FaShieldAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { generateMemberCardPdf } from "./utils/memberCardPdf";

import {
  formatShortDateFR,
  getAdherentAvatar,
  isLicenceValidated,
  isReglementValidated,
  toAbsoluteUrl,
} from "./utils/inscriptionUtils";

function StatusBadge({ checked, label }) {
  return (
    <div
      className={`inline-flex w-full items-center gap-2 rounded-2xl px-3 py-2.5 text-xs font-bold shadow-sm ${
        checked
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          : "bg-slate-100 text-slate-500 ring-1 ring-slate-200"
      }`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white ${
          checked ? "bg-emerald-600" : "bg-slate-400"
        }`}
      >
        {checked ? <FaCheck /> : <FaTimes />}
      </span>

      <span className="truncate">{label}</span>
    </div>
  );
}

function MemberCard({ adherent, dossier, settings, activeSeason }) {
  const reglementValidated = isReglementValidated(dossier);
  const licenceValidated = isLicenceValidated(dossier);

  const clubLogo = toAbsoluteUrl(settings?.logo_club || "");
  const avatar = getAdherentAvatar(adherent);

  const clubName = settings?.nom_club || "Club sportif";

  const season =
    adherent?.saison_active || activeSeason || settings?.saison_active || "—";

  return (
    <div className="relative aspect-[85.6/54] w-[920px] overflow-hidden rounded-[34px] bg-white text-slate-950 shadow-2xl ring-1 ring-slate-200">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(234,179,8,0.16),transparent_30%),linear-gradient(135deg,#ffffff_0%,#f8fafc_48%,#ffffff_100%)]" />

      <div className="absolute -left-12 top-0 h-36 w-[360px] -skew-x-[32deg] bg-slate-950" />
      <div className="absolute left-28 top-0 h-36 w-12 -skew-x-[32deg] bg-yellow-500" />

      <div className="absolute right-4 top-24 h-64 w-64 rounded-full border-[22px] border-slate-100/90" />
      <div className="absolute left-0 top-44 h-72 w-20 rounded-r-full bg-slate-100/80" />

      <div className="relative z-10 grid h-full grid-cols-[340px_minmax(0,1fr)] gap-6 p-7">
        <div className="flex h-full flex-col">
          <div className="overflow-hidden rounded-[28px] border-4 border-yellow-500 bg-slate-100 shadow-lg">
            <img
              src={avatar}
              alt={`${adherent?.prenom || ""} ${adherent?.nom || ""}`}
              crossOrigin="anonymous"
              className="h-[330px] w-full object-cover"
            />
          </div>

          <div className="mt-4 rounded-[22px] bg-slate-950 px-5 py-3 text-center shadow-lg">
            <p className="text-lg font-bold uppercase tracking-[0.14em] text-white">
              Saison <span className="text-yellow-400">{season}</span>
            </p>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2">
            <StatusBadge checked={reglementValidated} label="Règlement ok" />
            <StatusBadge checked={licenceValidated} label="Licence prise" />
          </div>
        </div>

        <div className="flex min-w-0 flex-col">
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0 pt-4">
              <p className="text-3xl font-bold uppercase tracking-[0.08em] text-slate-950">
                Carte de membre
              </p>

              <div className="mt-3 h-1 w-72 rounded-full bg-yellow-500" />

              <div className="mt-6">
                <h1 className="max-w-[520px] text-5xl font-bold leading-tight tracking-tight text-slate-950">
                  {clubName}
                </h1>
              </div>
            </div>

            <div className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-2 shadow-lg ring-1 ring-slate-200">
              {clubLogo ? (
                <img
                  src={clubLogo}
                  alt={clubName}
                  crossOrigin="anonymous"
                  className="h-full w-full object-contain"
                />
              ) : (
                <FaShieldAlt className="text-6xl text-yellow-500" />
              )}
            </div>
          </div>

          <div className="mt-8 rounded-[24px] bg-white/95 p-4 shadow-sm ring-1 ring-slate-100">
            <div className="space-y-3">
              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaUser className="text-slate-950" />
                  Nom :
                </p>

                <p className="truncate text-[1.85rem] font-semibold uppercase text-slate-950">
                  {adherent?.nom || "—"}
                </p>
              </div>

              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaUser className="text-slate-950" />
                  Prénom :
                </p>

                <p className="truncate text-[1.85rem] font-semibold text-slate-950">
                  {adherent?.prenom || "—"}
                </p>
              </div>

              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaBirthdayCake className="text-slate-950" />
                  Naissance :
                </p>

                <p className="text-[1.85rem] font-semibold text-slate-950">
                  {formatShortDateFR(adherent?.date_naissance)}
                </p>
              </div>

              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaIdCard className="text-slate-950" />
                  Licence :
                </p>

                <p className="truncate rounded-xl bg-white px-3 py-1.5 text-[1.45rem] font-semibold text-slate-950 shadow-sm">
                  {adherent?.licence_number || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MemberCardPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const location = useLocation();

  const [generating, setGenerating] = useState(false);

  const state = location.state || {};

  const adherent = state.adherent || null;
  const dossier = state.dossier || null;
  const settings = state.settings || {};

  const activeSeason =
    state.activeSeason ||
    settings?.saison_active ||
    adherent?.saison_active ||
    "";

  const canAccess = adherent && isReglementValidated(dossier);

  const handleDownloadPdf = async () => {
    if (!adherent) {
      toast.error("Aucun adhérent disponible pour générer la carte.");
      return;
    }

    try {
      setGenerating(true);
      toast.info("Génération de la carte en cours...");

      await generateMemberCardPdf({
        adherent,
        dossier,
        settings,
        activeSeason,
        mode: "save",
      });

      toast.success("Carte membre téléchargée.");
    } catch (error) {
      console.error("Erreur téléchargement carte membre :", error);
      toast.error(
        error?.message || "Impossible de générer le PDF de la carte membre.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = async () => {
    if (!adherent) {
      toast.error("Aucun adhérent disponible pour imprimer la carte.");
      return;
    }

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      toast.error("La fenêtre d’impression a été bloquée par le navigateur.");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Préparation impression</title>
        </head>
        <body style="font-family: Arial, sans-serif; padding: 24px;">
          Préparation de la carte membre...
        </body>
      </html>
    `);

    try {
      setGenerating(true);
      toast.info("Préparation de l’impression...");

      await generateMemberCardPdf({
        adherent,
        dossier,
        settings,
        activeSeason,
        mode: "print",
        targetWindow: printWindow,
      });

      toast.success("Fenêtre d’impression ouverte.");
    } catch (error) {
      console.error("Erreur impression carte membre :", error);

      printWindow.close();

      toast.error(error?.message || "Impossible d’imprimer la carte membre.");
    } finally {
      setGenerating(false);
    }
  };

  if (!adherent) {
    return (
      <section className="min-h-screen bg-slate-100 px-4 py-8 font-satoshi dark:bg-slate-950">
        <div className="mx-auto max-w-3xl rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            Carte membre
          </p>

          <h1 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
            Aucun adhérent sélectionné
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Impossible de générer une carte membre sans les informations de
            l’adhérent.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
          >
            <FaArrowLeft />
            Retour
          </button>
        </div>
      </section>
    );
  }

  if (!canAccess) {
    return (
      <section className="min-h-screen bg-slate-100 px-4 py-8 font-satoshi dark:bg-slate-950">
        <div className="mx-auto max-w-3xl rounded-[28px] border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose-600">
            Accès refusé
          </p>

          <h1 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
            Règlement non validé
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            La carte membre est disponible uniquement lorsque le règlement
            licence-cotisation est validé par le club.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
          >
            <FaArrowLeft />
            Retour
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-slate-100 px-4 py-8 font-satoshi text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-col gap-3 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-yellow-600">
              Carte membre
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
              {adherent.prenom} {adherent.nom}
            </h1>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Carte générée avec la photo, la licence, la saison active et le
              dossier d’inscription.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaArrowLeft />
              Retour
            </button>

            <button
              type="button"
              onClick={handlePrint}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            >
              <FaPrint />
              Imprimer
            </button>

            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaDownload />
              {generating ? "Génération..." : "Télécharger PDF"}
            </button>
          </div>
        </div>

        <div className="overflow-auto rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto w-fit origin-top scale-[0.66] sm:scale-[0.78] md:scale-90 lg:scale-100">
            <MemberCard
              adherent={adherent}
              dossier={dossier}
              settings={settings}
              activeSeason={activeSeason}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
